import type { CompletedForm, FormEntry } from "@rosc/contracts";

// The clinician's edits while reviewing a filled form, kept in the sheet
// until "Create PDF" sends them in one update.
export type FormReviewState = {
  // Field key → edited value (null: cleared).
  values: Record<string, string | null>;
  // Uncertain values accepted as they are.
  accepted: string[];
  // Identifier field key → typed value.
  identifiers: Record<string, string>;
};

export const emptyReview = (form?: CompletedForm | null): FormReviewState => ({
  values: {},
  accepted: [],
  identifiers: { ...(form?.identifiers ?? {}) },
});

/** The value shown for a field: the edit, else what ROSC filled. */
export const shownValue = (
  entry: FormEntry,
  review: FormReviewState,
): string | null =>
  Object.hasOwn(review.values, entry.fieldKey)
    ? (review.values[entry.fieldKey] ?? null)
    : entry.value;

/** Uncertain values not yet accepted, edited or cleared, in form order. */
export const pendingKeys = (
  entries: readonly FormEntry[],
  review: FormReviewState,
): string[] =>
  entries
    .filter(
      (entry) =>
        entry.status === "uncertain" &&
        !entry.reviewed &&
        !review.accepted.includes(entry.fieldKey) &&
        !Object.hasOwn(review.values, entry.fieldKey),
    )
    .map((entry) => entry.fieldKey);

export const accept = (
  review: FormReviewState,
  key: string,
): FormReviewState =>
  review.accepted.includes(key)
    ? review
    : { ...review, accepted: [...review.accepted, key] };

export const setValue = (
  review: FormReviewState,
  key: string,
  value: string | null,
): FormReviewState => ({
  ...review,
  values: { ...review.values, [key]: value },
});

export const setIdentifier = (
  review: FormReviewState,
  key: string,
  value: string,
): FormReviewState => ({
  ...review,
  identifiers: { ...review.identifiers, [key]: value },
});

/** The update body: only real changes, identifiers trimmed. */
export const reviewBody = (
  form: CompletedForm,
  review: FormReviewState,
  complete: boolean,
) => {
  const values: Record<string, string | null> = {};
  for (const entry of form.entries) {
    if (!Object.hasOwn(review.values, entry.fieldKey)) continue;
    const next = review.values[entry.fieldKey]?.trim() || null;
    if (next !== entry.value || entry.status === "uncertain")
      values[entry.fieldKey] = next;
  }
  const identifiers: Record<string, string> = {};
  for (const [key, value] of Object.entries(review.identifiers))
    identifiers[key] = value.trim();
  return { values, accepted: review.accepted, identifiers, complete };
};
